/**
 * Pestaña Honorarios — Otros cargos (notas de captura mensual del cliente).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DespachoInfoCallout,
  DespachoKpiCard,
  DespachoSectionCard,
  DespachoSimpleTable,
} from "@/components/despacho/DespachoUi";
import {
  despachoFiscalInfoApi,
  parseFiscalInfoError,
} from "@/modules/despacho/despachoFiscalInfoApi";

const MES_FULL = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function formatPeriodLabel(periodKey) {
  if (!periodKey || !/^\d{4}-\d{2}$/.test(periodKey)) return periodKey || "—";
  const [y, m] = periodKey.split("-");
  return `${MES_FULL[Number(m) - 1] || m} ${y}`;
}

function formatDate(value) {
  const raw = String(value || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw || "—";
  const [y, m, d] = raw.split("-");
  return `${d}/${m}/${y}`;
}

export default function HonorariosOtrosCargosPanel({
  readOnly = false,
  periodFilter = "",
}) {
  const periodKey = useMemo(() => {
    if (periodFilter && /^\d{4}-\d{2}$/.test(periodFilter)) return periodFilter;
    return "";
  }, [periodFilter]);

  const [items, setItems] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [onlyPending, setOnlyPending] = useState(true);
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await despachoFiscalInfoApi.listOtrosCargos({
        periodKey: periodKey || undefined,
        onlyPending,
      });
      setItems(data.items || []);
      setPendingCount(data.pending_count || 0);
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
      setItems([]);
      setPendingCount(0);
    } finally {
      setLoading(false);
    }
  }, [periodKey, onlyPending]);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggle = async (row, checked) => {
    if (readOnly || !row?.id || busyId) return;
    setBusyId(row.id);
    try {
      await despachoFiscalInfoApi.patchOtrosCargosCobrado(row.id, checked);
      toast.success(checked ? "Marcado como cobrado." : "Marcado como pendiente.");
      await load();
    } catch (err) {
      toast.error(parseFiscalInfoError(err).message);
    } finally {
      setBusyId("");
    }
  };

  const columns = [
    {
      key: "client",
      label: "Cliente",
      render: (row) => (
        <div className="min-w-0">
          <p className="text-xs font-medium text-[#0F2D5C] m-0 truncate">{row.client_name || "—"}</p>
          {row.client_rfc ? (
            <p className="text-[10px] text-slate-500 m-0 font-mono">{row.client_rfc}</p>
          ) : null}
        </div>
      ),
    },
    {
      key: "period",
      label: "Mes",
      render: (row) => (
        <span className="text-xs text-slate-700">{formatPeriodLabel(row.period_key)}</span>
      ),
    },
    {
      key: "description",
      label: "Descripción / motivo",
      render: (row) => (
        <p className="text-xs text-slate-800 m-0 whitespace-pre-wrap max-w-md">
          {row.description || "—"}
        </p>
      ),
    },
    {
      key: "noted_at",
      label: "Fecha",
      render: (row) => (
        <span className="text-xs text-slate-600">{formatDate(row.noted_at || row.updated_at)}</span>
      ),
    },
    {
      key: "cobrado",
      label: "Cobrado",
      render: (row) => (
        <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
          <Checkbox
            checked={Boolean(row.cobrado)}
            disabled={readOnly || busyId === row.id}
            onCheckedChange={(v) => handleToggle(row, Boolean(v))}
            aria-label={`Cobrado ${row.client_name || ""}`}
            data-testid={`otros-cargos-cobrado-${row.id}`}
          />
          <span>{row.cobrado ? formatDate(row.cobrado_at) || "Sí" : "No"}</span>
        </label>
      ),
    },
  ];

  return (
    <div data-testid="despacho-honorarios-otros-cargos">
      <DespachoInfoCallout>
        Resumen de <strong>otros cargos</strong> capturados en el seguimiento mensual del cliente
        (caja «Otros cargos al cliente»). Sirve para facturar o cobrar conceptos adicionales al honorario.
      </DespachoInfoCallout>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 my-4">
        <DespachoKpiCard label="Pendientes de cobro" value={pendingCount} tone="warn" />
        <DespachoKpiCard label="En listado" value={items.length} tone="neutral" />
        <DespachoKpiCard
          label="Filtro mes"
          value={periodKey ? formatPeriodLabel(periodKey) : "Todos"}
          tone="neutral"
        />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-3">
        <label className="inline-flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
          <Checkbox
            checked={onlyPending}
            onCheckedChange={(v) => setOnlyPending(Boolean(v))}
            data-testid="otros-cargos-only-pending"
          />
          <span>Solo pendientes</span>
        </label>
        {periodKey ? (
          <p className="text-[11px] text-slate-500 m-0">
            Filtrado por mes de trabajo: {formatPeriodLabel(periodKey)}.
          </p>
        ) : (
          <p className="text-[11px] text-slate-500 m-0">
            Elija mes en Facturas o deje vacío para ver todos los periodos con nota.
          </p>
        )}
      </div>

      <DespachoSectionCard
        title="Otros cargos por facturar / cobrar"
        description={`${items.length} registro(s)`}
        testId="otros-cargos-section"
      >
        {loading ? (
          <p className="text-sm text-slate-500 inline-flex items-center gap-2 m-0 py-4">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
          </p>
        ) : (
          <DespachoSimpleTable
            columns={columns}
            rows={items}
            emptyLabel={
              onlyPending
                ? "No hay otros cargos pendientes. Capture la nota en el detalle mensual del cliente."
                : "No hay otros cargos registrados."
            }
          />
        )}
      </DespachoSectionCard>
    </div>
  );
}
